import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, CheckCircle } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { apiService } from '../../services/apiService';
import { validateEmail, validatePhone, generatePassword } from '../../utils/helpers';

const AddClient = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [createdClient, setCreatedClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    password: generatePassword(),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Le nom est requis';
    if (!formData.company) newErrors.company = "Le nom de l'entreprise est requis";
    if (!formData.email) {
      newErrors.email = "L'email est requis";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    if (!formData.phone) {
      newErrors.phone = 'Le téléphone est requis';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Le numéro de téléphone n'est pas valide";
    }
    if (!formData.address) newErrors.address = "L'adresse est requise";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await apiService.createClient(formData);
      setCreatedClient({
        name: formData.name,
        email: formData.email,
        password: formData.password,
      });
    } catch (error) {
      console.error('Failed to create client:', error);
      setErrors({ submit: 'Erreur lors de la création du client' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  if (createdClient) {
    return (
      <div className="space-y-6 max-w-2xl">
        <Card className="p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Client créé</h1>
          <p className="text-gray-600 mb-6">
            Communiquez ces identifiants au client (aucun email automatique n&apos;est envoyé).
          </p>
          <div className="text-left space-y-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase">Email de connexion</p>
              <div className="flex items-center justify-between gap-2 mt-1">
                <code className="text-sm text-gray-900 break-all">{createdClient.email}</code>
                <Button type="button" variant="outline" size="sm" icon={Copy} onClick={() => copyToClipboard(createdClient.email)}>
                  Copier
                </Button>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase">Mot de passe temporaire</p>
              <div className="flex items-center justify-between gap-2 mt-1">
                <code className="text-sm text-gray-900 break-all">{createdClient.password}</code>
                <Button type="button" variant="outline" size="sm" icon={Copy} onClick={() => copyToClipboard(createdClient.password)}>
                  Copier
                </Button>
              </div>
            </div>
          </div>
          <Button type="button" variant="primary" className="mt-8 w-full sm:w-auto" onClick={() => navigate('/clients')}>
            Retour à la liste des clients
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="outline"
          icon={ArrowLeft}
          onClick={() => navigate('/clients')}
          className="mb-4"
        >
          Retour
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Ajouter un Client</h1>
        <p className="text-gray-600 mt-1">Créez un nouveau compte client</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Informations personnelles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nom complet"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Ilyas"
              error={errors.name}
              required
            />
            <Input
              label="Entreprise"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Ex: SNCF Réseau"
              error={errors.company}
              required
            />
            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="ilyas@entreprise.com"
              error={errors.email}
              required
            />
            <Input
              label="Téléphone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Ex: +33 6 12 34 56 78"
              error={errors.phone}
              required
            />
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Adresse</h2>
          <div className="space-y-4">
            <Input
              label="Adresse"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Ex: 12 rue de la Gare"
              error={errors.address}
              required
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Ville"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Ex: Paris"
              />
              <Input
                label="Code postal"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                placeholder="Ex: 75001"
              />
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Mot de passe temporaire
          </h2>
          <div className="space-y-2">
            <Input
              label="Mot de passe"
              name="password"
              value={formData.password}
              onChange={handleChange}
              readOnly
            />
            <p className="text-sm text-gray-500">
              Ce mot de passe sera affiché après la création : transmettez-le au client (email ou téléphone).
              Il pourra le modifier depuis son profil après connexion.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFormData({ ...formData, password: generatePassword() })}
            >
              Générer un nouveau mot de passe
            </Button>
          </div>
        </Card>

        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/clients')}
          >
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Créer le client
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddClient;

